"use client";

import { CalendarDays, Video, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  title: "1. Initial Consultation",
  description: "Schedule a private session to discuss your financial goals, tax history, and specific compliance requirements.",
  cta: "Book a session",
  stickerColor: "bg-white",
  Icon: CalendarDays,
  offsetClass: "md:mt-0"
}, {
  number: "02",
  title: "2. Strategic Review",
  description: "Our experts analyze your documents to identify potential deductions and ensure long-term tax efficiency.",
  cta: "View our services",
  stickerColor: "bg-white",
  Icon: Video,
  offsetClass: "md:mt-6"
}, {
  number: "03",
  title: "3. Ongoing Compliance",
  description: "Receive year-round support and proactive planning to maintain stability and peace of mind.",
  cta: "Learn about planning",
  stickerColor: "bg-white",
  Icon: Trophy,
  offsetClass: "md:mt-0"
}];
export default function YearRoundAdvisory() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: pathRef,
    isVisible: pathVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1313" id="year-round-advisory" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div ref={headRef} className="max-w-[720px] mx-auto text-center transition-all duration-700 ease-out" style={{
        opacity: headVisible ? 1 : 0,
        transform: headVisible ? "translateY(0)" : "translateY(16px)",
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="inline-block w-1 h-1 bg-accent" />
            <span className="font-mono text-[0.875rem] tracking-[0.1em] uppercase text-secondary font-medium">
              § 01 · Our Process
            </span>
            <span className="inline-block w-1 h-1 bg-accent" />
          </div>

          <h2 className="font-default font-semibold text-[clamp(2rem,5vw,3.5rem)] leading-[1.1] tracking-tighter">
            Your path to financial{" "}
            <span className="relative inline-block italic text-secondary">
              clarity
            </span>
          </h2>

          <p className="mt-6 mx-auto max-w-[56ch] text-[clamp(1.125rem,1.5vw,1.25rem)] leading-[1.6] text-secondary" style={{
          fontFamily: "var(--font-default)"
        }}>
            We guide you through complex tax landscapes with precision, ensuring your financial future is built on a foundation of compliance and strategic foresight.
          </p>
        </div>

        <div ref={cardsRef} className="relative mt-20 md:mt-24">
          <div ref={pathRef} className="hidden md:block absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none z-0" aria-hidden="true">
            <svg viewBox="0 0 1200 200" className="w-full h-[200px]" preserveAspectRatio="none" fill="none">
              <path d="M 80 100 Q 250 30, 420 100 T 780 100 Q 950 170, 1120 100" stroke="hsl(72 100% 45%)" strokeWidth="1" strokeLinecap="round" strokeDasharray="8 6" pathLength={1} style={{
              strokeDasharray: pathVisible ? "8 6" : "0 1000",
              transition: "stroke-dasharray 1200ms ease-out"
            }} />
            </svg>
          </div>

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((step, i) => {
            const Icon = step.Icon;
            return <div key={i} data-index={i} className={`group relative ${step.offsetClass} transition-all duration-700 ease-out`} style={{
              opacity: cardsVisible ? 1 : 0,
              transform: cardsVisible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: `${i * 100}ms`,
              transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
            }}>
                  <Card className="relative bg-card border-l-[1px] border-border p-8 transition-all duration-300 ease-out group-hover:bg-accent/10 group-hover:border-l-[4px] group-hover:border-accent">
                    <div aria-hidden="true" className="absolute top-4 left-4 font-mono font-medium text-secondary/[0.1] pointer-events-none select-none leading-none text-4xl">
                      {step.number}
                    </div>

                    <CardContent className="relative px-0">
                      <div className="relative inline-flex items-center justify-center mb-6">
                        <div className="relative w-12 h-12 flex items-center justify-center">
                          <Icon className="w-8 h-8 text-accent" strokeWidth={1.5} />
                        </div>
                      </div>

                      <h3 className="font-default font-semibold text-[clamp(1.25rem,3vw,1.75rem)] leading-[1.3] tracking-tighter mb-3">
                        {step.title}
                      </h3>

                      <p className="text-[clamp(0.875rem,1vw,1rem)] leading-[1.5] text-secondary mb-6">
                        {step.description}
                      </p>

                      <Link to={i === 0 ? "/GetStartedPage" : i === 1 ? "/TaxSolutionsPage" : "/OurPhilosophyPage"} className="group/link relative inline-flex items-center text-foreground font-medium text-sm tracking-wide uppercase">
                        <span className="relative">
                          {step.cta}
                        </span>
                        <span className="ml-2 transition-transform duration-300 group-hover/link:translate-x-1">→</span>
                      </Link>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}